/**
 * Voice Color - Deterministic default color generation for brand_voices.
 *
 * Pure functions. Matches the Postgres backfill logic in
 * supabase/migrations/20260512110000_brand_voices_color_and_use_cases.sql
 *
 * Postgres uses: abs(hashtext(id::text)) % 12
 * JavaScript uses: djb2-style hash on the UUID string mod 12
 *
 * NOTE: The two hash functions produce different numeric hashes - they are NOT
 * bit-for-bit identical. Both are deterministic per id. The Postgres backfill
 * runs once over existing rows; new client-inserted rows get a DB DEFAULT of
 * '#3B82F6', then the client follow-up UPDATE applies its own deterministic
 * color via this utility. From the user's perspective, every voice has a
 * stable color tied to its UUID - that is the only invariant that matters.
 *
 * Per D-02. Palette mirrored from apps/web/src/components/common/ColorPicker.tsx
 * lines 139-151.
 */

export const VOICE_COLOR_PALETTE = [
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#EF4444', // Red
  '#F59E0B', // Amber
  '#10B981', // Green
  '#06B6D4', // Cyan
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#64748B', // Slate
  '#1F2937', // Gray
] as const;

/**
 * djb2 hash, returns unsigned 32-bit integer.
 * Simple, fast, deterministic, no dependencies.
 */
function djb2(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) + input.charCodeAt(i); // hash * 33 + c
    hash = hash | 0; // force 32-bit
  }
  return Math.abs(hash);
}

/**
 * Get the deterministic default voice color for a given voice id.
 * Same id always returns the same color across calls.
 */
export function getDefaultVoiceColor(id: string): string {
  if (!id) return VOICE_COLOR_PALETTE[0];
  const index = djb2(id) % VOICE_COLOR_PALETTE.length;
  return VOICE_COLOR_PALETTE[index];
}
